import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { getDailyVerse } from "../../bible/dailyVerse";

const DailyVerseCard = ({ onOpenChapter, edition }) => {
  const isEn = edition?.id === "en";
  const [offset, setOffset] = useState(0);
  const [copiedSnackbar, setCopiedSnackbar] = useState(false);

  const verse = useMemo(() => {
    return getDailyVerse(offset);
  }, [offset]);

  const verseText = isEn ? verse.textEn : verse.textZh;
  const verseRef = isEn ? verse.refEn : verse.refZh;

  const handleCopy = () => {
    const fullText = `“${verseText}” —— ${verseRef}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(fullText).then(() => {
        setCopiedSnackbar(true);
      });
    }
  };

  const handleNextVerse = () => {
    setOffset((prev) => prev + 1);
  };

  const handleOpenContext = () => {
    if (verse?.chapterId) {
      onOpenChapter(verse.chapterId, verse.verse);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
        overflow: "hidden",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)"
            : "linear-gradient(135deg, rgba(234, 179, 8, 0.05) 0%, rgba(255, 255, 255, 0.6) 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: "warning.main",
          boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
        },
      }}
    >
      {/* Background Decorative Quote Mark */}
      <FormatQuoteIcon
        sx={{
          position: "absolute",
          right: 8,
          bottom: -10,
          fontSize: 100,
          opacity: 0.05,
          pointerEvents: "none",
          transform: "rotate(180deg)",
        }}
      />

      <Box sx={{ mb: 1.5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <AutoAwesomeIcon
              sx={{
                fontSize: { xs: 18, sm: 20 },
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#facc15" : "#d97706",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#facc15" : "#b45309",
              }}
            >
              {isEn ? "Verse of the Day" : "今日经文"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title={isEn ? "Next Verse" : "换一句"}>
              <IconButton size="small" onClick={handleNextVerse}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isEn ? "Copy Verse" : "复制金句"}>
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Verse Body */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.7,
            color: "text.primary",
            fontStyle: "italic",
            mb: 1,
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            letterSpacing: "0.01em",
          }}
        >
          “{verseText}”
        </Typography>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            fontWeight: 700,
            color: "text.secondary",
            fontSize: "0.85rem",
          }}
        >
          —— {verseRef}
        </Typography>
      </Box>

      {/* Action Footer */}
      <Box sx={{ mt: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          size="small"
          onClick={handleOpenContext}
          startIcon={<MenuBookIcon fontSize="small" />}
          sx={{
            py: 0.75,
            borderRadius: 2,
            fontSize: "0.85rem",
            textTransform: "none",
            borderColor: "divider",
            "&:hover": {
              borderColor: "warning.main",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(250, 204, 21, 0.08)"
                  : "rgba(234, 179, 8, 0.08)",
            },
          }}
        >
          {isEn ? "Read in Chapter" : "阅读本章上下文"}
        </Button>
      </Box>

      <Snackbar
        open={copiedSnackbar}
        autoHideDuration={2000}
        onClose={() => setCopiedSnackbar(false)}
        message={isEn ? "Verse copied to clipboard" : "金句已复制到剪贴板"}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Paper>
  );
};

export default DailyVerseCard;
